import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type AiAliasType = "cliente" | "proveedor";

export interface AiAlias {
  id: string;
  ocr_text: string;
  official_name: string;
  type: AiAliasType;
  created_at: string;
}

export function useAiAliases() {
  const { orgId } = useAuth();

  return useQuery({
    queryKey: ["ai-aliases", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<AiAlias[]> => {
      if (!orgId) return [];

      const [cliRes, provRes] = await Promise.all([
        supabase
          .from("cliente_aliases")
          .select("id, nombre_ocr, created_at, clientes(nombre)")
          .eq("org_id", orgId)
          .order("created_at", { ascending: false }),
        supabase
          .from("proveedor_aliases")
          .select("id, nombre_ocr, created_at, proveedores(nombre)")
          .eq("org_id", orgId)
          .order("created_at", { ascending: false }),
      ]);

      if (cliRes.error) throw cliRes.error;
      if (provRes.error) throw provRes.error;

      const cliAliases: AiAlias[] = (cliRes.data ?? []).map((a: any) => ({
        id: a.id,
        ocr_text: a.nombre_ocr,
        official_name: a.clientes?.nombre ?? "—",
        type: "cliente" as const,
        created_at: a.created_at,
      }));

      const provAliases: AiAlias[] = (provRes.data ?? []).map((a: any) => ({
        id: a.id,
        ocr_text: a.nombre_ocr,
        official_name: a.proveedores?.nombre ?? "—",
        type: "proveedor" as const,
        created_at: a.created_at,
      }));

      return [...cliAliases, ...provAliases].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
  });
}

export function useDeleteAiAlias() {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();

  return useMutation({
    mutationFn: async ({ id, type }: { id: string; type: AiAliasType }) => {
      const table = type === "cliente" ? "cliente_aliases" : "proveedor_aliases";
      const { error } = await supabase.from(table).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-aliases", orgId] });
      queryClient.invalidateQueries({ queryKey: ["proveedor-aliases"] });
      queryClient.invalidateQueries({ queryKey: ["cliente-aliases"] });
    },
  });
}
